import { zodResolver } from '@hookform/resolvers/zod';
import { Card, Tag } from 'antd';
import { companyColors } from 'apps/investment/constants';
import {
  orderStatusColors,
  orderTypeColors,
  orderTypeLabels,
} from 'apps/investment/constants/fundOrder';
import DecimalInput from 'common/form/DecimalInput';
import NumberInput from 'common/form/NumberInput';
import { Button } from 'common/ui/button';
import { Input } from 'common/ui/input';
import { Label } from 'common/ui/label';
import { LabelTooltip } from 'common/ui/label-tooltip';
import Loading from 'common/ui/loading';
import { commonDictionary, fundDictionary } from 'config/dictionary';
import { fundExplanation } from 'config/explanation';
import moment from 'moment';
import React, { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { cn } from 'utils/cn';
import { formatCurrency, formatNumber } from 'utils/formatAmount';
import { formatDateTime } from 'utils/formatDate';
import { z } from 'zod';
import StatusPicker from './status-picker';
import { FundOrderTypes } from './types';

const updateSchema = z.object({
  fund_order_status: z.string().optional(),
  fund_payment_status: z.string().optional(),
  fund_order_time: z.string().optional(),
  fund_amount: z.number().optional(),
  fund_net_amount: z.number().optional(),
  fund_quantity: z.number().optional(),
  fund_matched_price: z.number().optional(),
  fund_fee_amount: z.number().optional(),
  fund_tax_amount: z.number().optional(),
});

type UpdateFormValues = z.infer<typeof updateSchema> & {
  status?: number;
};

type UpdateFormProps = {
  defaultValues?: Partial<FundOrderTypes> | null;
  onSubmit: (values: UpdateFormValues) => void;
  isSubmitting: boolean;
};

export const UpdateForm: React.FC<UpdateFormProps> = ({
  defaultValues,
  onSubmit,
  isSubmitting,
}) => {
  const safeDefaultValues = defaultValues ?? {};

  const {
    fund_order_status,
    fund_payment_status,
    fund_can_cancel,
    fund_updated_at,
    fund_order_closing_time,
    fund_code,
    amount,
    estimate_amount,
    net_amount,
    estimate_net_amount,
    quantity,
    estimate_quantity,
    matched_price,
    fee_amount,
    estimate_fee_amount,
    tax_amount,
    estimate_tax_amount,
    order_side,
    status,
    status_label,
    trading_account_manager,
  } = safeDefaultValues;

  const currentConfig = STATUS_CONFIG[fund_order_status] || {
    color: 'gray',
    description: 'Chưa có trạng thái lệnh',
    status: 0,
  };
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(updateSchema),
    defaultValues: safeDefaultValues,
  });

  const statusOptions = Object.entries(STATUS_CONFIG).map(([key, config]) => ({
    key,
    color: config.color,
    description: config.description,
    statusLabel: config.statusLabel,
    appStatusLabel: config.appStatusLabel,
    status: config.status,
  }));

  const handleFormSubmit = values => {
    // Convert datetime-local inputs to proper server format
    const convertedValues = {
      ...values,
      fund_order_time: convertDatetimeLocalToServer(values.fund_order_time),
    };

    onSubmit(convertedValues);
  };
  return (
    <div className="flex flex-col md:flex-row gap-6">
      {/* Main Form */}
      <div className="flex-1">
        <div className="flex items-baseline gap-2 justify-between">
          <h2 className="text-2xl font-bold">Cập nhật lệnh quỹ</h2>
          <div className="flex items-center gap-2">
            Tài khoản giao dịch
            <Tag color={companyColors[trading_account_manager]}>
              {trading_account_manager}
            </Tag>
          </div>
        </div>

        <Card
          className="my-4 shadow-md"
          style={{
            border: `1px solid ${companyColors[trading_account_manager]}`,
          }}
        >
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div
              className={`mb-6 p-4 border-l-4 rounded ${getBannerClasses(
                currentConfig.color
              )}`}
            >
              <div>
                Trạng thái hiện tại:{' '}
                <b className="text-lg">{currentConfig.description}</b>
              </div>
              <div>{currentConfig.info}</div>
              {fund_payment_status !== 'success' && (
                <div>
                  Có thể đặt trạng thái thanh toán thành <b>'success'</b>.
                </div>
              )}
            </div>

            <div className="mb-8">
              <Label htmlFor="fund_order_status">Trạng thái lệnh</Label>
              <Controller
                name="fund_order_status"
                control={control}
                render={({ field }) => (
                  <StatusPicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Chọn trạng thái"
                    options={statusOptions}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 items-end">
              <div>
                <LabelTooltip
                  label={
                    <Label htmlFor="fund_order_time">
                      {fundDictionary.fund_order_time}
                    </Label>
                  }
                  tooltipContent={
                    <div>
                      <div>{fundExplanation.fund_order_time}</div>
                      <div>{'fund_order_time'}</div>
                    </div>
                  }
                />

                <Controller
                  name="fund_order_time"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="fund_order_time"
                      type="datetime-local"
                      required
                      value={toDatetimeLocal(field.value)}
                      onChange={e => field.onChange(e.target.value)}
                    />
                  )}
                />
              </div>
              <div>
                <Label htmlFor="fund_payment_status">
                  Trạng thái thanh toán
                </Label>
                <Controller
                  name="fund_payment_status"
                  control={control}
                  render={({ field }) => (
                    <select
                      value={field.value}
                      onChange={field.onChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {paymentStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>

            {/* Completion Fields (always visible, always editable) */}
            <div className="mb-8">
              <h3 className="font-semibold mb-4 text-lg text-gray-800">
                Thông tin hoàn tất
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <LabelTooltip
                    label={
                      <Label htmlFor="fund_amount">
                        {fundDictionary.fund_amount}
                      </Label>
                    }
                    tooltipContent="fund_amount"
                  />

                  <Controller
                    name="fund_amount"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        id="fund_amount"
                        placeholder="Số tiền"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div>
                  <LabelTooltip
                    label={
                      <Label htmlFor="fund_net_amount">
                        {fundDictionary.fund_net_amount}
                      </Label>
                    }
                    tooltipContent="fund_net_amount"
                  />
                  <Controller
                    name="fund_net_amount"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        id="fund_net_amount"
                        placeholder="Số tiền thực nhận"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div>
                  <LabelTooltip
                    label={
                      <Label htmlFor="fund_quantity">
                        {fundDictionary.fund_quantity}
                      </Label>
                    }
                    tooltipContent="fund_quantity"
                  />
                  <Controller
                    name="fund_quantity"
                    control={control}
                    render={({ field }) => (
                      <DecimalInput
                        id="fund_quantity"
                        placeholder="Số lượng"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div>
                  <LabelTooltip
                    label={
                      <Label htmlFor="fund_matched_price">
                        {fundDictionary.fund_matched_price}
                      </Label>
                    }
                    tooltipContent="fund_matched_price"
                  />
                  <Controller
                    name="fund_matched_price"
                    control={control}
                    render={({ field }) => (
                      <DecimalInput
                        id="fund_matched_price"
                        placeholder="Giá khớp"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div>
                  <LabelTooltip
                    label={
                      <Label htmlFor="fund_fee_amount">
                        {fundDictionary.fund_fee_amount}
                      </Label>
                    }
                    tooltipContent="fund_fee_amount"
                  />
                  <Controller
                    name="fund_fee_amount"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        id="fund_fee_amount"
                        placeholder="Phí"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div>
                  <LabelTooltip
                    label={
                      <Label htmlFor="fund_tax_amount">
                        {fundDictionary.fund_tax_amount}
                      </Label>
                    }
                    tooltipContent="fund_tax_amount"
                  />
                  <Controller
                    name="fund_tax_amount"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        id="fund_tax_amount"
                        placeholder="Thuế"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-between">
              <Button
                type="button"
                variant="destructive"
                disabled={isSubmitting || !fund_can_cancel}
                className="relative"
                onClick={() => {
                  if (confirm('Bạn có chắc muốn hủy lệnh này không?')) {
                    onSubmit({
                      fund_order_status: 'cancel',
                      status: 6,
                    });
                  }
                }}
              >
                Hủy lệnh
                {isSubmitting && (
                  <div className="absolute left-1/2 -translate-x-1/2 z-10">
                    <Loading text="" />
                  </div>
                )}
              </Button>
              <div className="flex gap-2 justify-end">
                {/* <Button
                  variant="secondary"
                  type="button"
                  onClick={handleSubmit(handleSaveOnly)}
                  disabled={isSubmitting}
                  className="relative"
                >
                  Lưu thông tin
                  {isSubmitting && (
                    <div className="absolute left-1/2 -translate-x-1/2 z-10">
                      <Loading text="" />
                    </div>
                  )}
                </Button> */}

                <Button type="submit" className="relative">
                  Cập nhật trạng thái
                  {isSubmitting && (
                    <div className="absolute left-1/2 -translate-x-1/2 z-10">
                      <Loading text="" />
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>

      {/* Info Card */}
      <div className="w-full md:w-96">
        <Card
          className={cn('my-4 shadow-md', {
            'border-green-500': order_side === 'buy',
            'border-blue-500': order_side === 'sell',
          })}
        >
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className={`font-semibold`}>{fund_code}</div>

              <Tag color={orderTypeColors[order_side]}>
                {orderTypeLabels[order_side]}
              </Tag>
            </div>

            {/* Fund Information Group */}
            <div className="border border-gray-200 rounded-lg p-3 bg-green-50">
              <h3 className="font-semibold text-green-800 mb-3 text-sm">
                Thông tin lệnh từ quỹ
              </h3>
              <div className="space-y-2">
                <Tag color={currentConfig.color} className="text-sm text-wrap">
                  {currentConfig.statusLabel}
                </Tag>
                <InfoItem
                  label=""
                  value={fund_can_cancel ? 'Có thể hủy' : 'Không thể hủy'}
                  className="text-green-700"
                />
                <InfoItem
                  label="Trạng thái thanh toán"
                  value={fund_payment_status}
                  className="text-green-700"
                />

                <InfoItem
                  label={
                    <LabelTooltip
                      label={fundDictionary.fund_order_closing_time}
                      tooltipContent={
                        <div>
                          <div>{fundExplanation.fund_order_closing_time}</div>
                          <div>{'fund_order_closing_time'}</div>
                        </div>
                      }
                    />
                  }
                  value={formatDateTime(fund_order_closing_time)}
                  className="text-green-700"
                />
                <InfoItem
                  label={
                    <LabelTooltip
                      label={fundDictionary.fund_updated_at}
                      tooltipContent={
                        <div>
                          <div>{fundExplanation.fund_updated_at}</div>
                          <div>{'fund_updated_at'}</div>
                        </div>
                      }
                    />
                  }
                  value={formatDateTime(fund_updated_at)}
                  className="text-green-700"
                />
              </div>
            </div>

            {/* Stag Information Group */}
            <div className="border border-gray-200 rounded-lg p-3 bg-blue-50">
              <h3 className="font-semibold text-blue-800 mb-3 text-sm">
                Thông tin lệnh từ Stag
              </h3>
              <div className="space-y-2">
                <Tag color={orderStatusColors[status]} className="text-sm">
                  {status_label}-{status}
                </Tag>

                <InfoItem
                  label="Số lượng ước tính"
                  value={
                    isNaN(estimate_quantity)
                      ? '-'
                      : formatNumber(estimate_quantity)
                  }
                  className="text-blue-700"
                />
                <InfoItem
                  label="Số tiền ước tính"
                  value={formatCurrency(estimate_amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Số tiền thực nhận ước tính"
                  value={formatCurrency(estimate_net_amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Phí ước tính"
                  value={formatCurrency(estimate_fee_amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Thuế ước tính"
                  value={formatCurrency(estimate_tax_amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label={commonDictionary.quantity}
                  value={isNaN(quantity) ? '-' : formatNumber(quantity)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Số tiền"
                  value={formatCurrency(amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Số tiền thực nhận"
                  value={formatCurrency(net_amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Phí"
                  value={formatCurrency(fee_amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Thuế"
                  value={formatCurrency(tax_amount)}
                  className="text-blue-700"
                />
                <InfoItem
                  label="Giá khớp"
                  value={formatCurrency(matched_price)}
                  className="text-blue-700"
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const InfoItem = ({
  label,
  value,
  className,
}: {
  label: ReactNode;
  value: string;
  className?: string;
}) => (
  <div className="flex p-1 gap-1 items-baseline border-b border-border">
    <div className="text-xs font-medium text-gray-500">{label}</div>
    <div className={`font-medium ${className}`}>{value}</div>
  </div>
);

const STATUS_CONFIG = {
  new: {
    color: 'blue',
    statusLabel: 'Lệnh đã tạo phía Stag',
    description: 'Lệnh mới tạo.',
    appStatusLabel: 'Mua: Chờ thanh toán | Bán: Chờ khớp',
    nextStatus: 'waiting_admin_approve',
    status: 1,
    info: (
      <div>
        Trạng thái tiếp theo <b>Lệnh đã đẩy qua đối tác thành công</b>.
      </div>
    ),
  },
  waiting_admin_approve: {
    color: 'yellow',
    statusLabel: 'Lệnh đã đẩy qua đối tác thành công',
    description:
      'Lệnh đã đẩy qua đối tác thành công. Chờ đối tác nhập lệnh lên VSD.',
    appStatusLabel: 'Mua: Chờ xác nhận | Bán: -',
    nextStatus: 'approved',
    status: 2,
    info: (
      <div>
        Trạng thái tiếp theo <b>Đối tác đã nhập lệnh lên VSD thành công</b>.
      </div>
    ),
  },
  approved: {
    color: 'green',
    statusLabel: 'Đối tác đã nhập lệnh lên VSD thành công',
    description: 'Đối tác đã nhập lệnh lên VSD thành công.',
    appStatusLabel: 'Mua: Chờ khớp | Bán: Chờ khớp',
    nextStatus: 'pending_completion',
    status: 3,
    info: (
      <div>
        Trạng thái tiếp theo{' '}
        <b>Đã có kết quả khớp lệnh từ VSD trả về cho đối tác.</b> Vui lòng nhập
        các thông tin hoàn tất bên dưới nếu có.
      </div>
    ),
  },
  pending_completion: {
    color: 'orange',
    statusLabel: 'Đã có kết quả khớp lệnh từ VSD trả về cho đối tác',
    description: 'Đã có kết quả khớp lệnh từ VSD trả về cho đối tác.',
    appStatusLabel: 'Mua: Chờ khớp | Bán: Chờ khớp',
    nextStatus: 'completed',
    status: 4,
    info: (
      <div>
        Trạng thái tiếp theo <b>Lệnh đã khớp thành công và được phân phối.</b>{' '}
        Vui lòng nhập các thông tin hoàn tất bên dưới nếu có.
      </div>
    ),
  },
  completed: {
    color: 'cyan',
    statusLabel: 'Lệnh đã khớp thành công và được phân phối.',
    description: 'Lệnh đã khớp thành công và được phân phối.',
    appStatusLabel: 'Mua: Đã phân phối | Bán: Đã phân phối',
    nextStatus: '',
    status: 5,
    info: ' ',
  },
  cancel: {
    color: 'red',
    statusLabel: 'Lệnh đã bị hủy.',
    description: 'Lệnh đã bị hủy.',
    appStatusLabel: 'Mua: Đã hủy | Bán: Đã hủy',
    nextStatus: '',
    status: 6,
    info: '',
  },
};

// Utility to convert datetime-local input to server format
function convertDatetimeLocalToServer(value) {
  if (!value) return value;
  return moment(value)
    .utcOffset(7)
    .format('YYYY-MM-DDTHH:mm:ssZ');
}

// Utility to format ISO string to 'YYYY-MM-DDTHH:mm' for datetime-local input
function toDatetimeLocal(value) {
  if (!value) return '';
  const m = moment(value);
  if (!m.isValid()) return '';
  return m.format('YYYY-MM-DDTHH:mm');
}

function getBannerClasses(color) {
  switch (color) {
    case 'blue':
      return 'bg-blue-50 border-blue-400 text-blue-800';
    case 'yellow':
      return 'bg-yellow-50 border-yellow-400 text-yellow-800';
    case 'green':
      return 'bg-green-50 border-green-400 text-green-800';
    case 'orange':
      return 'bg-orange-50 border-orange-400 text-orange-800';
    case 'cyan':
      return 'bg-cyan-50 border-cyan-400 text-cyan-800';
    case 'red':
      return 'bg-red-50 border-red-400 text-red-800';
    default:
      return 'bg-gray-50 border-gray-400 text-gray-800';
  }
}

const paymentStatusOptions = [
  { label: 'pending', value: 'pending' },
  { label: 'success', value: 'success' },
];
